import React, { useState, useEffect } from 'react';
import {
  Card,
  CardBody,
  CardHeader,
  Avatar,
  Button,
  Input,
  Textarea,
  Chip,
  Progress,
  useDisclosure,
  Spinner,
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter
} from '@heroui/react';
import {
  Edit3,
  Camera,
  MessageCircle,
  Calendar,
  MapPin,
  Award,
  Users,
  FileText,
  MessageSquare,
  TrendingUp,
  Save,
  X
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import ImageUploader from '../Timeline/ImageUploader';
import Post from '../Timeline/Post';

const Profile = ({ userId, onClose }) => {
  const { user: currentUser } = useAuth();
  const [profileUser, setProfileUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  
  // Edit form state
  const [editForm, setEditForm] = useState({
    username: '',
    first_name: '',
    last_name: '',
    summary: ''
  });
  
  // Avatar editing
  const [selectedImage, setSelectedImage] = useState(null);
  
  // Activity data
  const [activityData, setActivityData] = useState([]);
  const [stats, setStats] = useState({
    postCount: 0,
    commentCount: 0,
    averageObjectivity: 0,
    influencePoints: 0,
    availableInvites: 0
  });

  const { isOpen: isAvatarModalOpen, onOpen: onAvatarModalOpen, onClose: onAvatarModalClose } = useDisclosure();
  const { isOpen: isPostModalOpen, onOpen: onPostModalOpen, onClose: onPostModalClose } = useDisclosure();
  
  // Selected post for modal
  const [selectedPost, setSelectedPost] = useState(null);

  useEffect(() => {
    fetchProfileData();
  }, [userId]);

  const fetchProfileData = async () => {
    try {
      setLoading(true);
      const targetUserId = userId || currentUser._id;
      const isOwnProfile = !userId || targetUserId === currentUser._id;
      
      // Use 'me' endpoint for own profile, user ID for others
      const endpoint = isOwnProfile ? '/api/v1/users/me' : `/api/v1/users/${targetUserId}`;
      
      const response = await fetch(`${endpoint}?include_area=true&include_counts=true&include_activity=true&include_objectivity=true`, {
        credentials: 'include'
      });
      
      if (response.ok) {
        const data = await response.json();
        const userData = data.users;
        
        setProfileUser(userData);
        setEditForm({
          username: userData.username || '',
          first_name: userData.first_name || '',
          last_name: userData.last_name || '',
          summary: userData.summary || ''
        });
        
        setStats({
          postCount: userData.post_count || 0,
          commentCount: userData.comment_count || 0,
          averageObjectivity: userData.average_objectivity || 0,
          influencePoints: userData.points || 0,
          availableInvites: Math.max(0, 10 - (userData.invite_count || 0))
        });
        
        // Process activity data
        if (userData.activity) {
          setActivityData(userData.activity.slice(0, 10)); // Show last 10 activities
        }
      } else {
        console.error('Failed to fetch profile data');
      }
    } catch (error) {
      console.error('Error fetching profile data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleActivityClick = (activity) => {
    setSelectedPost(activity);
    onPostModalOpen();
  };

  const handleSaveProfile = async () => {
    try {
      setSaving(true);
      
      const formData = new FormData();
      formData.append('user[username]', editForm.username);
      formData.append('user[first_name]', editForm.first_name);
      formData.append('user[last_name]', editForm.last_name);
      formData.append('user[summary]', editForm.summary);
      
      // Note: Avatar is handled separately in handleImageSelect
      
      const response = await fetch(`/api/v1/users/me`, {
        method: 'PUT',
        credentials: 'include',
        body: formData
      });
      
      if (response.ok) {
        const data = await response.json();
        setProfileUser(data.users);
        setEditing(false);
        console.log('Profile updated successfully');
      } else {
        console.error('Failed to update profile');
        alert('Erro ao atualizar perfil');
      }
    } catch (error) {
      console.error('Error updating profile:', error);
      alert('Erro de rede ao atualizar perfil');
    } finally {
      setSaving(false);
    }
  };

  const handleImageSelect = async (imageData) => {
    console.log('Profile: Image selected:', imageData);
    setSelectedImage(imageData);
    
    // Immediately save the avatar
    try {
      setSaving(true);
      
      const formData = new FormData();
      
      // Convert canvas to blob for upload
      if (imageData.canvas) {
        imageData.canvas.toBlob(async (blob) => {
          formData.append('user[avatar]', blob, 'avatar.png');
          
          const response = await fetch('/api/v1/users/me', {
            method: 'PUT',
            credentials: 'include',
            body: formData
          });
          
          if (response.ok) {
            const data = await response.json();
            console.log('Profile: Avatar updated successfully');
            
            // Update profile user data
            setProfileUser(data.users);
            
            // Clear selected image since it's now saved
            setSelectedImage(null);
            
            // Refresh the page to update all avatar instances
            window.location.reload();
          } else {
            console.error('Failed to update avatar');
            alert('Erro ao atualizar avatar');
          }
        }, 'image/png', 0.9);
      }
    } catch (error) {
      console.error('Error updating avatar:', error);
      alert('Erro de rede ao atualizar avatar');
    } finally {
      setSaving(false);
    }
  };

  const getObjectivityColor = (score) => {
    if (score >= 80) return 'success';
    if (score >= 60) return 'warning';
    if (score >= 40) return 'warning';
    return 'danger';
  };

  const getRoleColor = (role) => {
    if (!role) return 'default';
    switch (role.name) {
      case 'Fundador': return 'secondary';
      case 'Moderador': return 'primary';
      default: return 'default';
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('pt-BR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const isOwnProfile = profileUser && currentUser && profileUser._id === currentUser._id;

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Spinner size="lg" color="primary" />
      </div>
    );
  }

  if (!profileUser) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Card>
          <CardBody>
            <p>Usuário não encontrado</p>
          </CardBody>
        </Card>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto p-6 space-y-6">
      {/* Header Card */}
      <Card className="relative rounded-nextui">
        <CardBody className="p-6">
          {onClose && (
            <Button
              isIconOnly
              variant="light"
              className="absolute top-4 right-4 z-10"
              onPress={onClose}
            >
              <X className="w-4 h-4" />
            </Button>
          )}
          
          <div className="flex flex-col md:flex-row gap-6">
            {/* Avatar Section */}
            <div className="flex flex-col items-center space-y-4">
              <div className="relative">
                <Avatar
                  src={selectedImage?.dataURL || profileUser.avatar}
                  name={profileUser.username}
                  className="w-32 h-32 text-large"
                />
                {isOwnProfile && (
                  <Button
                    isIconOnly
                    size="sm"
                    color="primary"
                    className="absolute -bottom-2 -right-2"
                    onPress={onAvatarModalOpen}
                  >
                    <Camera className="w-4 h-4" />
                  </Button>
                )}
              </div>
              
              {!isOwnProfile && (
                <Button
                  color="primary"
                  variant="flat"
                  startContent={<MessageCircle className="w-4 h-4" />}
                  size="sm"
                >
                  Mensagem
                </Button>
              )}
            </div>

            {/* Profile Info */}
            <div className="flex-1 space-y-4">
              {editing ? (
                <div className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <Input
                      label="Nome"
                      value={editForm.first_name}
                      onChange={(e) => setEditForm({...editForm, first_name: e.target.value})}
                    />
                    <Input
                      label="Sobrenome"
                      value={editForm.last_name}
                      onChange={(e) => setEditForm({...editForm, last_name: e.target.value})}
                    />
                  </div>
                  <Input
                    label="Nome de usuário"
                    value={editForm.username}
                    onChange={(e) => setEditForm({...editForm, username: e.target.value})}
                  />
                  <Textarea
                    label="Sobre mim"
                    value={editForm.summary}
                    onChange={(e) => setEditForm({...editForm, summary: e.target.value})}
                    rows={4}
                  />
                </div>
              ) : (
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <h1 className="text-2xl font-bold">
                        {profileUser.first_name} {profileUser.last_name}
                      </h1>
                      <p className="text-gray-600">@{profileUser.username}</p>
                    </div>
                    {isOwnProfile && (
                      <Button
                        variant="light"
                        startContent={<Edit3 className="w-4 h-4" />}
                        onPress={() => setEditing(true)}
                      >
                        Editar
                      </Button>
                    )}
                  </div>

                  {/* Role and Area */}
                  <div className="flex flex-wrap gap-2">
                    {profileUser.role && (
                      <Chip color={getRoleColor(profileUser.role)} variant="flat">
                        {profileUser.role.name}
                      </Chip>
                    )}
                    {profileUser.area && (
                      <Chip variant="flat" startContent={<MapPin className="w-3 h-3" />}>
                        {profileUser.area.name}
                      </Chip>
                    )}
                  </div>

                  {/* Summary */}
                  {profileUser.summary && (
                    <p className="text-gray-700 leading-relaxed">
                      {profileUser.summary}
                    </p>
                  )}

                  {/* Join Date */}
                  <div className="flex items-center text-sm text-gray-500">
                    <Calendar className="w-4 h-4 mr-2" />
                    Ingressou em {formatDate(profileUser.created_at)}
                  </div>
                </div>
              )}

              {editing && (
                <div className="flex gap-2">
                  <Button
                    color="primary"
                    startContent={<Save className="w-4 h-4" />}
                    onPress={handleSaveProfile}
                    isLoading={saving}
                  >
                    Salvar
                  </Button>
                  <Button
                    variant="light"
                    onPress={() => {
                      setEditing(false);
                      setSelectedImage(null);
                    }}
                  >
                    Cancelar
                  </Button>
                </div>
              )}
            </div>
          </div>
        </CardBody>
      </Card>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardBody className="text-center p-4">
            <Award className="w-8 h-8 mx-auto mb-2 text-yellow-500" />
            <p className="text-2xl font-bold text-yellow-600">{stats.influencePoints}</p>
            <p className="text-sm text-gray-600">Pontos de Influência</p>
          </CardBody>
        </Card>

        <Card>
          <CardBody className="text-center p-4">
            <TrendingUp className="w-8 h-8 mx-auto mb-2 text-blue-500" />
            <p className="text-2xl font-bold text-blue-600">{stats.averageObjectivity}%</p>
            <p className="text-sm text-gray-600">Objetividade Média</p>
            <Progress
              value={stats.averageObjectivity}
              color={getObjectivityColor(stats.averageObjectivity)}
              size="sm"
              className="mt-2"
            />
          </CardBody>
        </Card>

        <Card>
          <CardBody className="text-center p-4">
            <FileText className="w-8 h-8 mx-auto mb-2 text-green-500" />
            <p className="text-2xl font-bold text-green-600">{stats.postCount}</p>
            <p className="text-sm text-gray-600">Posts</p>
          </CardBody>
        </Card>

        <Card>
          <CardBody className="text-center p-4">
            <MessageSquare className="w-8 h-8 mx-auto mb-2 text-purple-500" />
            <p className="text-2xl font-bold text-purple-600">{stats.commentCount}</p>
            <p className="text-sm text-gray-600">Comentários</p>
          </CardBody>
        </Card>
      </div>

      {/* Invites Card (only for own profile) */}
      {isOwnProfile && (
        <Card>
          <CardHeader>
            <div className="flex items-center">
              <Users className="w-5 h-5 mr-2" />
              <h3 className="text-lg font-semibold">Convites</h3>
            </div>
          </CardHeader>
          <CardBody>
            <div className="flex items-center justify-between">
              <p className="text-gray-600">Convites disponíveis</p>
              <Chip color="primary" variant="flat">
                {stats.availableInvites}
              </Chip>
            </div>
          </CardBody>
        </Card>
      )}

      {/* Recent Activity */}
      <Card>
        <CardHeader>
          <h3 className="text-lg font-semibold">Atividade Recente</h3>
        </CardHeader>
        <CardBody>
          {activityData.length > 0 ? (
            <div className="space-y-3">
              {activityData.map((activity, index) => (
                <div 
                  key={activity._id || index} 
                  className="flex items-start space-x-3 p-3 hover:bg-gray-50 rounded-lg cursor-pointer transition-colors"
                  onClick={() => handleActivityClick(activity)}
                >
                  <FileText className="w-4 h-4 mt-1 text-gray-400" />
                  <div className="flex-1">
                    <p className="font-medium line-clamp-2">
                      {activity.title || activity.text}
                    </p>
                    <p className="text-sm text-gray-500 mt-1">
                      {formatDate(activity.created_at)}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-gray-500 text-center py-8">Nenhuma atividade recente</p>
          )}
        </CardBody>
      </Card>

      {/* Post Detail Modal */}
      <Modal 
        isOpen={isPostModalOpen} 
        onClose={onPostModalClose}
        size="3xl"
        scrollBehavior="inside"
      >
        <ModalContent>
          <ModalHeader className="flex flex-col gap-1">
            <h3 className="text-lg font-semibold">Detalhes do Post</h3>
            <p className="text-sm text-gray-500">
              {selectedPost && formatDate(selectedPost.created_at)}
            </p>
          </ModalHeader>
          <ModalBody className="px-0">
            {selectedPost && (
              <div className="px-6">
                <Post 
                  post={selectedPost}
                  currentUser={currentUser}
                  isModal={true}
                />
              </div>
            )}
          </ModalBody>
          <ModalFooter>
            <Button 
              color="primary" 
              variant="light" 
              onPress={onPostModalClose}
            >
              Fechar
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Avatar Editor Modal */}
      <ImageUploader
        isOpen={isAvatarModalOpen}
        onClose={onAvatarModalClose}
        onImageSave={handleImageSelect}
      />
    </div>
  );
};

export default Profile;
